import type { RefObject } from 'react'
import type { FC } from 'react'

import type { ComboboxRef } from '@skytech/ui'
import { Combobox, Input, LoaderCircle, Select } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field } from 'formik'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { ADMIN_ROLES, USER_ROLE, useHasRoleBasedAccess } from '@/hooks/access'
import queries from '@/modules/admin/queries'
import type { Employee } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'

export const UserInfo: FC<{
  selectedCustomerId: number | undefined
  comboboxMethodsRef: RefObject<ComboboxRef>
}> = ({ selectedCustomerId, comboboxMethodsRef }) => {
  const translate = useTranslation()
  const hasLocationsAccess = useHasRoleBasedAccess([
    ...ADMIN_ROLES,
    USER_ROLE.IT_USER,
    USER_ROLE.IT_PURCHASER,
  ])

  const { data: assetTypes } = useQuery(queries.adminAssetTypes(), adminResources.getAssetTypes)
  const { data: employees, isLoading: isEmployeesLoading } = useQuery(
    queries.adminEmployeeList(hasLocationsAccess),
    () => adminResources.getEmployees(hasLocationsAccess)
  )

  const employeeFiltered =
    employees?.filter(employee => {
      const isVisible = !employee.isHidden
      const isSelectable =
        employee.customerId === selectedCustomerId || !employee.customerId || !selectedCustomerId
      return isVisible && isSelectable
    }) || []

  if (isEmployeesLoading) {
    return (
      <div className="mx-2 grid grid-cols-2 gap-4 rounded-lg">
        <LoaderCircle />
      </div>
    )
  }

  const locationUserTypeId = assetTypes?.userTypes.find(type => type.type === 'LOCATION')?.id

  return (
    <div className="mx-2 grid grid-cols-2 gap-x-4 rounded-lg">
      <Field name="userName">
        {({ field, meta, form }: FieldProps) => (
          <Combobox
            isFullWidth
            capitalizeLabel
            comboboxMethodsRef={comboboxMethodsRef}
            id="userName"
            hitsTranslation={<Translation id="hit(s)" />}
            noResultsFoundTranslation={<Translation id="No results found" />}
            marginBottom={16}
            label={
              <>
                <Translation id="Username" />*
              </>
            }
            ariaLabel={translate('Username')}
            list={employeeFiltered}
            stringMatcher={(item: Employee | null) => (item ? `${item.name} - ${item.code}` : '')}
            touched={meta.touched}
            error={meta.error}
            placeholder={translate('Choose employee...')}
            onSetFieldValue={(v: string) => {
              form.setFieldValue('userName', v)
              if (!v) return
              // We display username and phone number but send only the username
              const parsedUserName = v.split(' -')[0]
              const selectedEmployee = employees?.find(employee => employee.name === parsedUserName)
              const isLocationUser = selectedEmployee?.isLocation
              if (!isLocationUser) return
              if (typeof locationUserTypeId !== 'number')
                throw Error('User type id is not a number!')
              form.setFieldValue('userTypeId', locationUserTypeId)
              form.setFieldValue('customerAddressId', selectedEmployee.customerAddressId)
            }}
            onSetFieldTouched={() => form.setFieldTouched('userName')}
            {...field}
          />
        )}
      </Field>

      <Field name="userTypeId">
        {({ field, form }: FieldProps) => (
          <Select
            capitalizeLabel
            id="userTypeId"
            label={
              <>
                <Translation id="User type" />*
              </>
            }
            options={
              assetTypes?.userTypes.map(({ id, type }) => ({
                value: id,
                label: translate(`asset_user_type/${type}`),
              })) || []
            }
            marginBottom={16}
            {...field}
            onChange={option => form.setFieldValue(field.name, option?.value)}
          />
        )}
      </Field>

      <Field name="received">
        {({ field, meta }: FieldProps) => (
          <Input
            capitalizeLabel
            isFullWidth
            id="received"
            ariaLabel={translate('Received')}
            label={
              <>
                <Translation id="Received" />*
              </>
            }
            marginBottom={16}
            touched={meta.touched}
            error={meta.error}
            type="date"
            className="cursor-text"
            {...field}
          />
        )}
      </Field>
    </div>
  )
}
